import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Pencil, Trash2, PlayCircle, RotateCw, Video as VideoIcon, Loader2, Eye, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/common/PageHeader";
import { SearchBar } from "@/components/common/SearchBar";
import { DataTable, Column } from "@/components/tables/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ConfirmDeleteDialog } from "@/components/common/ConfirmDeleteDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useListState } from "@/hooks/useListState";
import { 
  useGetVideosQuery, 
  useCreateVideoMutation, 
  useUpdateVideoMutation, 
  useDeleteVideoMutation, 
  useUploadVideoChunkMutation,
  Video 
} from "@/redux/slices/videoApi";
import { useGetChaptersQuery } from "@/redux/slices/chapterApi";
import { useGetSubjectsQuery } from "@/redux/slices/subjectApi";
import { useGetAcademicYearsQuery } from "@/redux/slices/academicYearApi";
import { format } from "date-fns";

const schema = z.object({
  title: z.string().trim().min(2, "Title required").max(140),
  description: z.string().max(500).optional().default(""),
  videoType: z.enum(["local", "external"]),
  platform: z.enum(["youtube", "other"]),
  url: z.string().min(1, "Video source required"),
  thumbnail: z.string().optional().default(""),
  duration: z.string().optional().default(""),
  academicYearId: z.string().min(1, "Year required"),
  subjectId: z.string().min(1, "Subject required"),
  chapterId: z.string().min(1, "Chapter required"),
  isActive: z.boolean().default(true),
});
type FormValues = z.infer<typeof schema>;

export default function VideosPage() {
  const list = useListState();
  const navigate = useNavigate();
  const { data, isFetching, refetch } = useGetVideosQuery(list.queryParams());
  const { data: yearsData } = useGetAcademicYearsQuery({ limit: 100 });
  const { data: subjectsData } = useGetSubjectsQuery({ limit: 1000 });
  const { data: chaptersData } = useGetChaptersQuery({ limit: 1000 });
  
  const [addVideo, addM] = useCreateVideoMutation();
  const [updateVideo, updateM] = useUpdateVideoMutation();
  const [deleteVideo, deleteM] = useDeleteVideoMutation();
  const [uploadChunk] = useUploadVideoChunkMutation();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Video | null>(null);
  const [toDelete, setToDelete] = useState<Video | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { 
      title: "", description: "", videoType: "external", platform: "youtube", 
      url: "", thumbnail: "", duration: "", academicYearId: "", subjectId: "", 
      chapterId: "", isActive: true 
    },
  });

  const watchType = form.watch("videoType");
  const watchYear = form.watch("academicYearId");
  const watchSubject = form.watch("subjectId");

  const filteredSubjects = useMemo(() => {
    if (!subjectsData?.data || !watchYear) return [];
    return subjectsData.data.filter(s => s.academicYearId === watchYear);
  }, [subjectsData, watchYear]);

  const filteredChapters = useMemo(() => {
    if (!chaptersData?.data || !watchSubject) return [];
    return chaptersData.data.filter(c => c.subjectId === watchSubject);
  }, [chaptersData, watchSubject]);

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(0);
    
    const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const fileName = file.name.replace(/\s+/g, '-');

    try {
      for (let i = 0; i < totalChunks; i++) {
        const chunk = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const formData = new FormData();
        formData.append("chunk", chunk);
        formData.append("chunkIndex", i.toString());
        formData.append("totalChunks", totalChunks.toString());
        formData.append("fileName", fileName);

        const res = await uploadChunk({ 
          formData, 
          folder: "videos", 
          subfolder: "lectures" 
        }).unwrap();
        setUploadProgress(Math.round(((i + 1) / totalChunks) * 100));

        if (res.merged && res.url) {
          form.setValue("url", res.path || "");
          toast.success("Video uploaded successfully");
        }
      }
    } catch {
      toast.error("Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    form.reset({ 
      title: "", description: "", videoType: "external", platform: "youtube", 
      url: "", thumbnail: "", duration: "", academicYearId: "", subjectId: "", 
      chapterId: "", isActive: true 
    });
    setOpen(true);
  };

  const openEdit = (v: Video) => {
    setEditing(v);
    form.reset(v as any);
    setOpen(true);
  };

  const onSubmit = async (v: FormValues) => {
    try {
      if (editing) {
        await updateVideo({ id: editing.id, body: v }).unwrap();
        toast.success("Video updated");
      } else {
        await addVideo(v).unwrap();
        toast.success("Video added");
      }
      setOpen(false);
    } catch {
      toast.error("Something went wrong");
    }
  };

  const columns: Column<Video>[] = [
    { 
      key: "title", 
      header: "Video Title", 
      render: (r) => (
        <div className="flex items-center gap-3">
          <div 
            className="flex h-9 w-9 shrink-0 items-center justify-center cursor-pointer rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
            onClick={() => navigate(`/videos/${r.id}`)}
          >
            <PlayCircle className="h-5 w-5" />
          </div>
          <div>
            <p className="font-medium">{r.title}</p>
            <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-tight">{r.platform} • {r.duration || 'N/A'}</p>
          </div>
        </div>
      ) 
    },
    { key: "year", header: "Year", render: (r) => <span className="text-muted-foreground">{r.academicYear?.name || "—"}</span> },
    { key: "subject", header: "Subject", render: (r) => <span className="text-muted-foreground">{r.subject?.name || "—"}</span> },
    { key: "chapter", header: "Chapter", render: (r) => <span className="text-muted-foreground">{r.chapter?.name || "—"}</span> },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.isActive ? "active" : "inactive"} /> },
    { key: "createdAt", header: "Created", render: (r) => format(new Date(r.createdAt), "dd MMM yyyy") },
  ];

  return (
    <div className="min-h-screen bg-muted/20">
      <PageHeader
        title="Video Lectures"
        description="Manage Ayurveda video content and live links"
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching}>
              <RotateCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
            </Button>
            <Button onClick={openCreate} className="shadow-lg shadow-primary/20">
              <Plus className="mr-1.5 h-4 w-4" /> Add Video
            </Button>
          </div>
        }
      />
      <div className="space-y-6 p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col gap-4 rounded-2xl border bg-card p-4 shadow-sm lg:flex-row lg:items-center">
          <SearchBar 
            value={list.search} 
            onChange={list.setSearch} 
            placeholder="Search videos by title..." 
            className="flex-1" 
          />
          
          <div className="flex flex-wrap items-center gap-3">
            <Select value={list.filters.academicYearId || "all"} onValueChange={(v) => list.setFilter("academicYearId", v === "all" ? undefined : v)}>
              <SelectTrigger className="w-[140px]"><SelectValue placeholder="All Years" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {yearsData?.data?.map(y => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={list.filters.subjectId || "all"} onValueChange={(v) => list.setFilter("subjectId", v === "all" ? undefined : v)}>
              <SelectTrigger className="w-[140px]"><SelectValue placeholder="All Subjects" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subjects</SelectItem>
                {subjectsData?.data?.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DataTable
          columns={columns}
          rows={data?.data ?? []}
          loading={isFetching}
          total={data?.total ?? 0}
          page={list.page}
          limit={list.limit}
          onPageChange={list.setPage}
          onLimitChange={list.setLimit}
          actions={(row) => (
            <div className="flex justify-end gap-1">
              <Button 
                size="icon" 
                variant="ghost" 
                onClick={() => navigate(`/videos/${row.id}`)}
              >
                <Eye className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" onClick={() => openEdit(row)}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" onClick={() => setToDelete(row)} className="text-destructive">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[95vh] max-w-2xl flex-col p-0 overflow-hidden shadow-2xl border-none rounded-2xl">
          <DialogHeader className="p-8 pb-4 bg-primary/5">
            <DialogTitle className="text-2xl font-bold">{editing ? "Edit Video Lecture" : "Upload New Lecture"}</DialogTitle>
            <DialogDescription className="font-medium text-muted-foreground">Configure video sources and educational metadata.</DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto px-8 py-4">
            <form id="video-form" onSubmit={form.handleSubmit(onSubmit)} className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="title" className="text-sm font-semibold">Video Title</Label>
                <Input id="title" className="h-11 shadow-sm" placeholder="e.g. Chapter 1: Introduction to Ayurveda" {...form.register("title")} />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold">Video Source</Label>
                <Select
                  value={form.watch("videoType")}
                  onValueChange={(v) => {
                    form.setValue("videoType", v as any);
                    form.setValue("url", "");
                    if (v === 'local') form.setValue("platform", "other");
                  }}
                >
                  <SelectTrigger className="h-11 shadow-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="external">Live / External Link</SelectItem>
                    <SelectItem value="local">Local Upload (Secure)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {watchType === 'external' ? (
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Platform</Label>
                  <Select
                    value={form.watch("platform")}
                    onValueChange={(v) => form.setValue("platform", v as any)}
                  >
                    <SelectTrigger className="h-11 shadow-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="youtube">YouTube</SelectItem>
                      <SelectItem value="other">Other Platform</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2 text-right">
                  {/* Empty div for grid alignment */}
                </div>
              )}

              <div className="space-y-2 sm:col-span-2">
                <Label className="text-sm font-semibold">{watchType === 'local' ? "Select Video File" : "Source URL"}</Label>
                {watchType === 'local' ? (
                  <div className="space-y-3 rounded-xl border border-dashed p-4 bg-muted/30">
                    <div className="flex items-center gap-3">
                      <Input 
                        type="file" 
                        accept="video/*" 
                        onChange={handleVideoUpload}
                        disabled={isUploading}
                        className="cursor-pointer bg-background"
                      />
                      {isUploading && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
                    </div>
                    {isUploading && (
                      <div className="space-y-1.5">
                        <div className="flex justify-between text-[10px] font-bold uppercase">
                          <span>Uploading Chunks...</span>
                          <span>{uploadProgress}%</span>
                        </div>
                        <Progress value={uploadProgress} className="h-1.5 shadow-inner" />
                      </div>
                    )}
                    {form.watch("url") && !isUploading && (
                      <p className="flex items-center gap-2 text-xs text-green-600 font-bold uppercase italic">
                        <StatusBadge status="active" /> File ready: {form.watch("url").split('/').pop()}
                      </p>
                    )}
                  </div>
                ) : (
                  <Input className="h-11 shadow-sm" placeholder="https://youtube.com/watch?v=..." {...form.register("url")} />
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold">Academic Year</Label>
                <Select value={form.watch("academicYearId") || undefined} onValueChange={(v) => {
                  form.setValue("academicYearId", v);
                  form.setValue("subjectId", "");
                  form.setValue("chapterId", "");
                }}>
                  <SelectTrigger className="h-11 shadow-sm"><SelectValue placeholder="Select Year" /></SelectTrigger>
                  <SelectContent>
                    {yearsData?.data?.map(y => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold">Subject</Label>
                <Select value={form.watch("subjectId") || undefined} onValueChange={(v) => {
                  form.setValue("subjectId", v);
                  form.setValue("chapterId", "");
                }} disabled={!watchYear}>
                  <SelectTrigger className="h-11 shadow-sm"><SelectValue placeholder="Select Subject" /></SelectTrigger>
                  <SelectContent>
                    {filteredSubjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold">Chapter</Label>
                <Select value={form.watch("chapterId") || undefined} onValueChange={(v) => form.setValue("chapterId", v)} disabled={!watchSubject}>
                  <SelectTrigger className="h-11 shadow-sm"><SelectValue placeholder="Select Chapter" /></SelectTrigger>
                  <SelectContent>
                    {filteredChapters.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="duration" className="text-sm font-semibold">Duration (Optional)</Label>
                <Input id="duration" className="h-11 shadow-sm" placeholder="e.g. 45:00" {...form.register("duration")} />
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="description" className="text-sm font-semibold">Brief Description</Label>
                <Textarea id="description" rows={3} className="resize-none shadow-sm" placeholder="Summary of topics covered in this lecture..." {...form.register("description")} />
              </div>
            </form>
          </div>

          <DialogFooter className="border-t p-8 bg-muted/10">
            <Button variant="ghost" onClick={() => setOpen(false)} className="h-11 font-bold uppercase text-xs px-6">Discard</Button>
            <Button form="video-form" type="submit" disabled={addM.isLoading || updateM.isLoading || isUploading} className="h-11 px-8 rounded-xl font-bold uppercase tracking-wider text-xs shadow-xl shadow-primary/20 group">
              {editing ? "Save Changes" : "Publish Lecture"}
              <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        onConfirm={async () => { if (toDelete) { await deleteVideo(toDelete.id).unwrap(); toast.success("Lecture removed successfully"); setToDelete(null); } }}
        loading={deleteM.isLoading}
        title="Permanently remove lecture?"
        description={`"${toDelete?.title}" and its associated data will be deleted. This cannot be undone.`}
      />
    </div>
  );
}
